import {
  Switch,
  theme,
  Space,
  Select,
  Typography,
  Button,
  Input,
  Modal,
  Collapse,
  Segmented,
  Breadcrumb,
  Card,
  Spin,
  Drawer,
  Steps,
  Radio,
} from "antd";
import { useRouter } from "next/router";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { useEffectOnce } from "usehooks-ts";
import {
  BoardCardData,
  DeepLinkType,
  DigestSection,
  DigestV2State,
  isExploreCard,
} from "pages/api/deep-links/types";
import { AuthedPage } from "pages/AuthedPage";
import { Flex } from "src/components/Flex";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { Condition, SearchState } from "src/search/types";
import { Duration, isDuration } from "src/utils/date";
import { FixedHeaderScrollBody } from "src/components/FixedHeaderScrollBody";
import { useSnippets } from "src/hooks/useSnippets";
import { BoardHeader } from "../../boards/BoardHeader";
import { BoardBody } from "../../boards/BoardBody";
import { useApplicationContext } from "src/hooks/useApplication";
import { DigestPrompt } from "../DigestPrompt";
import { Board } from "../../boards/[boardId].page";
import Markdown from "react-markdown";
import { useQuery } from "react-query";
import { getResultSummariesBulk } from "pages/api-clients/graph-search";
import { asKey, fromKey } from "src/utils/fields";
import { DigestLinks } from "../DigestLinks";
import Link from "next/link";
import {
  PrefixGlobalKey,
  DefaultPrefixKeys,
} from "pages/api-clients/narrative-prefix";

function useDigest() {
  const router = useRouter();
  const app = useApplicationContext();
  const digests = useDeepLinks<DigestV2State>(DeepLinkType.DIGEST_2);
  const link = digests.links.data?.find(
    (digest) => digest.id === router.query.topicId
  );

  const orgId = `${router.query.orgId || ""}`;
  const duration = `${router.query.duration || ""}`;
  const startDate = `${router.query.startDate || ""}`;
  const endDate = `${router.query.endDate || ""}`;
  const filters = useMemo(() => {
    try {
      return JSON.parse(`${router.query.filters || "[]"}`);
    } catch {
      return [];
    }
  }, [router.query.filters]);
  const globalState: SearchState = useMemo(
    () => ({
      duration: isDuration(duration) ? duration : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      filters,
      orgId,
    }),
    [filters, duration, startDate, endDate, orgId]
  );
  const seed = useMemo(
    () => `${duration}-${startDate}-${endDate}-${JSON.stringify(filters)}`,
    [duration, startDate, endDate, filters]
  );

  const setGlobalState = useCallback(
    ({
      duration: newDuration = "",
      startDate: newStartDate = "",
      endDate: newEndDate = "",
      filters: newFilters = [],
    }: SearchState) => {
      if (
        duration !== newDuration ||
        startDate !== newStartDate ||
        endDate !== newEndDate ||
        JSON.stringify(filters) !== JSON.stringify(newFilters)
      ) {
        router.push({
          pathname: router.pathname,
          query: {
            ...router.query,
            duration: newDuration || undefined,
            startDate: newStartDate || undefined,
            endDate: newEndDate || undefined,
            filters: JSON.stringify(
              (newFilters?.length || 0) > 0 ? newFilters : undefined
            ),
          },
        });
      } else {
        // console.log("ignoring noop update");
      }
    },
    [router, duration, startDate, endDate, filters]
  );

  const save = useCallback(
    async ({
      id,
      name,
      description,
      scope,
      thumbnail,
      ...state
    }: {
      id: string;
      name: string;
      description?: string;
      scope?: "private" | "workspace" | "global";
      thumbnail?: string;
      prompt?: string;
    } & DigestV2State) => {
      const toUpdate = { description, thumbnail, id, name, scope, state };
      if (!description) delete toUpdate.description;
      if (!scope) delete toUpdate.scope;
      digests.update.mutateAsync(toUpdate).then(() => {
        /* message.info(`Updated "${newName}"`); */
      });
    },
    [digests.update]
  );

  const boardIsGlobal = link && !link.orgId;
  const boardIsShared = link && !boardIsGlobal && !link.userId;
  const boardIsPrivate = link && !boardIsGlobal && !boardIsShared;
  const userCanEdit =
    (boardIsGlobal && app.isSuperAdmin) ||
    (boardIsShared && app.isOrgAdmin) ||
    boardIsPrivate;

  return {
    id: `${router.query.topicId}`,
    globalState,
    link,
    notFound: digests.links.isFetched && !link,
    orgId,
    save,
    saving: digests.update.isLoading,
    seed,
    setGlobalState,
    userCanEdit,
  };
}
export type Digest = ReturnType<typeof useDigest>;

export default function DigestPage() {
  return <AuthedPage orgId title="Digest" main={() => <DigestLoader />} />;
}

function DigestLoader() {
  const pivotsReady = useSnippets().isFetched;
  const digest = useDigest();
  if (!digest.link || !pivotsReady) return <LoadingPage />;
  if (digest.notFound) return <NotFoundPage />;
  return <DigestInit digest={digest} />;
}

function DigestInit({ digest }: { digest: Digest }) {
  const app = useApplicationContext();
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;

  const [ready, setReady] = useState(false);
  useEffect(() => {
    app.setTitle(digest.link?.name || "");
  }, [app, digest.link?.name]);
  useEffectOnce(() => {
    const initialParams: Record<string, string> = {
      duration: router.query.duration ? `${router.query.duration}` : "",
      startDate: router.query.startDate ? `${router.query.startDate}` : "",
      endDate: router.query.endDate ? `${router.query.endDate}` : "",
      filters: router.query.filters ? `${router.query.filters}` : "",
    };
    const paramKeys = Object.keys(router.query);
    const hasDuration = paramKeys.includes("duration");
    const hasFilters = paramKeys.includes("filters");
    const hasEndDate = paramKeys.includes("endDate");
    const hasStartDate = paramKeys.includes("startDate");
    if (
      !initialParams.filters &&
      !hasFilters &&
      digest.link?.json.defaultFilters &&
      digest.link.json.defaultFilters.length > 0
    ) {
      initialParams.filters = JSON.stringify(digest.link.json.defaultFilters);
    }
    if (
      !initialParams.duration &&
      !hasDuration &&
      (!router.query.startDate || !router.query.endDate) &&
      digest.link?.json.defaultDateRange?.duration
    ) {
      initialParams.duration = digest.link.json.defaultDateRange.duration;
    } else if (
      (!initialParams.startDate || !initialParams.enddate) &&
      (!hasStartDate || !hasEndDate) &&
      digest.link?.json.defaultDateRange?.startDate &&
      digest.link.json.defaultDateRange.endDate
    ) {
      initialParams.startDate = digest.link.json.defaultDateRange.startDate;
      initialParams.endDate = digest.link.json.defaultDateRange.endDate;
    }

    if (
      !(hasDuration && hasStartDate && hasEndDate && hasFilters) ||
      initialParams.duration !== router.query.duration ||
      initialParams.startDate !== router.query.startDate ||
      initialParams.endDate !== router.query.endDate ||
      initialParams.filters !== router.query.filters
    ) {
      router
        .replace({
          pathname: router.pathname,
          query: { ...router.query, ...initialParams },
        })
        .then(() => setReady(true));
    } else {
      setReady(true);
    }
  });
  return ready ? <DigestReady digest={digest} /> : <LoadingPage />;
}

function DigestReady({ digest }: { digest: Digest }) {
  const [editing, setEditing] = useState(false);
  const router = useRouter();
  const [version, setVersion] = useState<"v1" | "v2">("v1");

  return (
    <PageContainer>
      <FixedHeaderScrollBody disableEffect>
        <Flex start gap={20}>
          <BoardHeader board={digest as Board} />

          <div style={{ padding: 16 }}>
            <Space>
              <Switch
                checked={editing}
                onChange={() => setEditing(!editing)}
                checkedChildren="Editing"
                unCheckedChildren="Previewing"
              />
              <Button
                type="primary"
                onClick={() => {
                  const defaultName = `New Topic ${new Date().toLocaleDateString()}`;
                  router.push({
                    pathname: `/${router.query.orgId}/topics/start`,
                    query: { name: defaultName, autoCreate: true },
                  });
                }}
              >
                New Topic
              </Button>
            </Space>
          </div>
          <Link
            href={`/${digest.orgId}/topics/${digest.id}`}
            style={{ padding: 16 }}
          >
            view
          </Link>
          <Radio.Group
            value={version}
            onChange={(e) => {
              setVersion(e.target.value);
              if (e.target.value === "v2") {
                router.push({
                  pathname: router.pathname.replace("edit", "edit.v2"),
                  query: router.query,
                });
              }
            }}
            buttonStyle="solid"
          >
            <Radio.Button value="v1">Version 1</Radio.Button>
            <Radio.Button value="v2">Version 2</Radio.Button>
          </Radio.Group>
        </Flex>
        {editing ? (
          <>
            Prompt
            <DigestPrompt digest={digest} />
            <hr />
            Links
            <DigestLinks digest={digest} />
            <hr />
            Queries
            <BoardBody board={digest as Board} />
          </>
        ) : (
          <RenderedDigest digest={digest} />
        )}
      </FixedHeaderScrollBody>
    </PageContainer>
  );
}

type GlobalState = {
  duration?: Duration;
  startDate?: string;
  endDate?: string;
  filters: Condition[];
};

// replicating the logic used by QuestionLoader to apply global filters to each query
export const figureQueryFragment = (
  card: BoardCardData,
  globalState: GlobalState
): Partial<SearchState> => {
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("expected question card");

  const mappedGlobalFilters = globalState.filters.map((filter) => {
    const unMappedKey = asKey(filter);
    const mapping = card.mappings?.find((m) => m.fromFieldKey === unMappedKey);
    if (!mapping) return filter;
    return { ...filter, ...fromKey(mapping.toFieldKey) };
  });

  return globalState.duration
    ? {
        duration: globalState.duration,
        filters: mappedGlobalFilters,
      }
    : globalState.startDate && globalState.endDate
    ? {
        startDate: globalState.startDate,
        endDate: globalState.endDate,
        filters: mappedGlobalFilters,
      }
    : {
        filters: mappedGlobalFilters,
      };
};

function RenderedDigest({ digest }: { digest: Digest }) {
  // running the query now
  const app = useApplicationContext();
  const orgId = app.org_id!;

  const router = useRouter();

  // const defaultFilters = digest.link?.json.defaultFilters || [];
  // replicating the logic used by QuestionLoader to get default filters from the router
  const globalDuration = router.query.duration as Duration | undefined;
  const globalStartDate = router.query.startDate as string | undefined;
  const globalEndDate = router.query.endDate as string | undefined;
  const globalFilters: Condition[] = useMemo(() => {
    try {
      return JSON.parse(`${router.query.filters || "[]"}`);
    } catch {
      return [];
    }
  }, [router.query.filters]);
  const globalState: GlobalState = {
    duration: globalDuration,
    startDate: globalStartDate,
    endDate: globalEndDate,
    filters: globalFilters,
  };

  const boardData: Record<string, BoardCardData> = digest.link?.json.data || {};
  const queryStates: SearchState[] = Object.values(boardData)
    .filter(isExploreCard)
    .map((card) => ({
      ...card.state.params,
      ...figureQueryFragment(card, globalState),
    }));

  const section: DigestSection = {
    prompt: digest.link?.json.prompt!,
    queries: queryStates,
  };

  const result = useQuery(
    [orgId, section],
    async () =>
      getResultSummariesBulk(
        {
          snippet: digest.globalState,
          sections: [
            {
              ...section,
              queries: section.queries.map((q) => ({ ...q, orgId })),
            },
          ],
        },
        digest.orgId,
        DefaultPrefixKeys.NARRATIVE
      ),
    { enabled: Boolean(orgId) && Boolean(section.prompt) }
  );

  return (
    <div style={{ padding: 16 }}>
      {result.isLoading ? <LoadingOutlined /> : ""}
      <Markdown>{result.data?.summary}</Markdown>
    </div>
  );
}

function LoadingPage() {
  return (
    <PageContainer>
      <Flex style={{ height: "100%" }}>
        <LoadingOutlined />
      </Flex>
    </PageContainer>
  );
}

function NotFoundPage() {
  return (
    <PageContainer>
      <Flex style={{ height: "100%" }}>404 not found</Flex>
    </PageContainer>
  );
}

function PageContainer({ children }: PropsWithChildren) {
  const { token } = theme.useToken();
  // console.log("render: PAGE_CONTAINER");
  return (
    <div
      style={{
        backgroundColor: token.colorBgContainer,
        height: "100%",
        width: "100%",
        overflow: "auto",
        borderRadius: 16,
      }}
    >
      {children}
    </div>
  );
}
